import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { ShoppingCart, Plus, MoreHorizontal, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { DateCell } from '@/components/DateCell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth-context';
import { FormDrawer } from '@/components/ui/form-drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

interface Order {
  id: string;
  orderNumber: string;
  accountId: string;
  status: string;
  billingStatus: string | null;
  total: string;
  subtotal: string;
  totalCost: string | null;
  grossProfit: string | null;
  marginPercent: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  account?: {
    name: string;
  };
}

interface Account {
  id: string;
  name: string;
}

const orderFormSchema = z.object({
  accountId: z.string().min(1, 'Please select an account'),
  poType: z.string().min(1, 'Please select a PO type'),
  customerPoNumber: z.string().optional(),
  notes: z.string().optional(),
});

type OrderFormData = z.infer<typeof orderFormSchema>;

const BILLING_STATUS_LABELS: Record<string, string> = {
  not_invoiced: 'Not Invoiced',
  invoiced_open: 'Open',
  invoiced_due: 'Due',
  invoiced_paid_partial: 'Partial',
  invoiced_paid_full: 'Paid',
  invoiced_overdue: 'Overdue',
};

export default function OrdersPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { sessionId } = useAuth();
  const queryClient = useQueryClient();
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter] = useState<string>('');

  const { data: ordersData, isLoading } = useQuery<{ orders: Order[]; total: number }>({
    queryKey: ['/api/orders', sessionId, { search, status: statusFilter }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      return api.get<{ orders: Order[]; total: number }>(`/orders?${params.toString()}`, sessionId);
    },
    enabled: !!sessionId,
  });

  const { data: accountsData } = useQuery<{ accounts: Account[] }>({
    queryKey: ['/api/accounts', sessionId],
    queryFn: async () => {
      return api.get<{ accounts: Account[] }>('/accounts?limit=500', sessionId);
    },
    enabled: !!sessionId,
  });

  const orders = ordersData?.orders || [];
  const accounts = accountsData?.accounts || [];

  const form = useForm<OrderFormData>({
    resolver: zodResolver(orderFormSchema),
    defaultValues: {
      accountId: '',
      poType: 'formal',
      customerPoNumber: '',
      notes: '',
    },
  });

  const createMutation = useMutation<Order, Error, OrderFormData>({
    mutationFn: async (data: OrderFormData) => {
      return await api.post<Order>('/orders', data, sessionId);
    },
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
      toast({
        title: 'Order created',
        description: 'Redirecting to add line items...',
      });
      setNewOrderOpen(false);
      form.reset();
      setLocation(`/orders/${order.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create order',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (orderId: string) => {
      await api.delete(`/orders/${orderId}`, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
      toast({
        title: 'Order deleted',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete order',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: OrderFormData) => {
    createMutation.mutate(data);
  };

  const columns = [
    {
      key: 'createdAt',
      header: 'Entry',
      sortable: true,
      cell: (row: Order) => (
        <DateCell value={row.createdAt} className="text-muted-foreground" />
      ),
    },
    {
      key: 'orderNumber',
      header: 'Order #',
      sortable: true,
      cell: (row: Order) => (
        <div className="font-medium">{row.orderNumber}</div>
      ),
    },
    {
      key: 'account',
      header: 'Account',
      cell: (row: Order) => (
        <span>{row.account?.name || '-'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row: Order) => <StatusBadge status={row.status} type="order" />,
    },
    {
      key: 'billingStatus',
      header: 'Billing',
      cell: (row: Order) => {
        const billingStatus = row.billingStatus || 'not_invoiced';
        const variant = billingStatus === 'invoiced_paid_full' ? 'default' 
          : billingStatus === 'invoiced_overdue' ? 'destructive' 
          : 'secondary';
        return (
          <Badge variant={variant}>
            {BILLING_STATUS_LABELS[billingStatus] || 'Not Invoiced'}
          </Badge>
        );
      },
    },
    {
      key: 'total',
      header: 'Total',
      className: 'text-right',
      cell: (row: Order) => (
        <span className="font-medium">${Number(row.total || 0).toLocaleString()}</span>
      ),
    },
    {
      key: 'marginPercent',
      header: 'Margin',
      className: 'text-right',
      cell: (row: Order) => (
        <span className="text-muted-foreground">{Number(row.marginPercent || 0).toFixed(1)}%</span>
      ),
    },
    {
      key: 'grossProfit',
      header: 'Profit',
      className: 'text-right',
      cell: (row: Order) => (
        <span className="text-green-600 dark:text-green-500">${Number(row.grossProfit || 0).toLocaleString()}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-10 text-right',
      cell: (row: Order) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  if (confirm(`Delete order ${row.orderNumber}?`)) {
                    deleteMutation.mutate(row.id);
                  }
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Order
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sales Orders"
        actions={
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64"
            />
            <Button size="sm" onClick={() => setNewOrderOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New Order
            </Button>
          </div>
        }
      />

      <DataTable
        data={orders}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(row) => setLocation(`/orders/${row.id}`)}
        emptyMessage="No orders found"
        emptyIcon={<ShoppingCart className="h-12 w-12 text-muted-foreground/50" />}
      />

      <FormDrawer
        open={newOrderOpen}
        onOpenChange={setNewOrderOpen}
        title="New Sales Order"
        description="Create a new order. You can add line items after creation."
        formId="create-order"
        isPending={createMutation.isPending}
        submitLabel="Create Order"
        size="md"
        data-testid="drawer-new-order"
      >
        <Form {...form}>
          <form
            id="create-order"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="accountId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder="Select an account" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="poType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>PO Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger variant="flat">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="formal">Formal</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="verbal">Verbal</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customerPoNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Customer PO #</FormLabel>
                    <FormControl>
                      <Input variant="flat" placeholder="PO-12345" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      variant="flat"
                      placeholder="Add any notes or special instructions..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
